// Domicilio del chofer: buscar la dirección, elegir el resultado correcto y
// guardarlo. Lo usan el perfil del chofer (path "/v2/driver-home/me") y su
// ficha en el panel admin ("/v2/driver-home/:id"). Se ve como capa en
// Choferes en vivo y se puede elegir como inicio o fin de un recorrido.

import React, { useEffect, useState } from "react";
import Button from "../ui/Button";
import { authFetch } from "../../lib/authFetch";

export type DriverHome = { address: string | null; latitude: number | null; longitude: number | null; updated_at?: string | null; updated_by?: string | null };
type Candidate = { latitude: number; longitude: number; displayName: string | null; address: string | null; locality: string | null };

const inputStyle: React.CSSProperties = {
  flex: 1, minWidth: "200px", minHeight: "42px", borderRadius: "12px", border: "1px solid var(--border)",
  background: "var(--surface-alt)", color: "var(--text-primary)", padding: "0 12px", font: "inherit", fontSize: "14px",
};
const mutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const smallButton: React.CSSProperties = { minHeight: "38px", padding: "6px 12px", fontSize: "13px" };
const candidateStyle = (on: boolean): React.CSSProperties => ({
  display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: "10px", cursor: "pointer",
  font: "inherit", fontSize: "13px", color: "var(--text-primary)", marginTop: "6px",
  border: `1px solid ${on ? "var(--primary, #4f8cff)" : "var(--border)"}`,
  background: on ? "var(--primary-soft, rgba(79,140,255,0.12))" : "transparent",
});

// Nombre corto para guardar: «Calle 123, Localidad» (no el display_name entero).
export function shortAddress(c: Candidate, typed: string): string {
  const line = [c.address, c.locality].filter(Boolean).join(", ");
  return (line || c.displayName || typed).slice(0, 255);
}

async function read(res: Response) {
  const body = (await res.json().catch(() => ({}))) ?? {};
  if (!res.ok || !body.ok) throw new Error(body.error || `Error ${res.status}`);
  return body;
}

export default function HomeAddressEditor({ token, path, editable = true }: { token: string | null; path: string; editable?: boolean }) {
  const [home, setHome] = useState<DriverHome | null>(null);
  const [text, setText] = useState("");
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [chosen, setChosen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    authFetch(path, { token }).then(read)
      .then((b) => { if (alive) setHome(b.home); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [path, token]);

  const search = async () => {
    if (text.trim().length < 3) { setError("Escribí la calle, el número y la localidad."); return; }
    setBusy(true); setError(""); setSaved(false); setChosen(null);
    try {
      const b = await read(await authFetch(`/v2/driver-home/geo/search?direccion=${encodeURIComponent(text.trim())}`, { token }));
      setCandidates(b.candidates ?? []);
      if (b.candidates?.length === 1) setChosen(0);
    } catch (e: any) {
      setError(e.message); setCandidates(null);
    } finally { setBusy(false); }
  };

  const save = async (body: object) => {
    setBusy(true); setError(""); setSaved(false);
    try {
      const b = await read(await authFetch(path, { token, json: true, method: "PUT", body: JSON.stringify(body) }));
      setHome(b.home); setCandidates(null); setChosen(null); setText(""); setSaved(true);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  const pick = chosen != null && candidates ? candidates[chosen] : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div>
        <div style={{ fontSize: "14px", color: "var(--text-primary)" }}>
          {home === null ? "Cargando…" : home.address || "Sin domicilio cargado"}
        </div>
        {home?.address && home.latitude != null && home.longitude != null ? (
          <a style={{ ...mutedStyle, color: "var(--primary, #4f8cff)" }} target="_blank" rel="noreferrer"
            href={`https://www.openstreetmap.org/?mlat=${home.latitude}&mlon=${home.longitude}#map=17/${home.latitude}/${home.longitude}`}>
            Ver en el mapa
          </a>
        ) : null}
        <div style={mutedStyle}>Se usa en el mapa de operación y como inicio o fin de los recorridos.</div>
      </div>

      {editable ? (
        <>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <input aria-label="Dirección del domicilio" style={inputStyle} value={text} placeholder="Calle y número, localidad"
              onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(); }} />
            <Button variant="secondary" style={smallButton} onClick={search} disabled={busy}>Buscar</Button>
            {home?.address ? <Button variant="ghost" style={smallButton} onClick={() => save({ address: null })} disabled={busy}>Borrar</Button> : null}
          </div>

          {candidates ? (
            candidates.length ? (
              <div>
                <div style={mutedStyle}>Elegí la que corresponde:</div>
                {candidates.map((c, i) => (
                  <button key={i} type="button" style={candidateStyle(chosen === i)} aria-pressed={chosen === i} onClick={() => setChosen(i)}>
                    {c.displayName || shortAddress(c, text)}
                  </button>
                ))}
              </div>
            ) : <div style={mutedStyle}>No se encontró esa dirección. Probá agregando la localidad.</div>
          ) : null}

          {pick ? (
            <div>
              <Button style={smallButton} disabled={busy}
                onClick={() => save({ address: shortAddress(pick, text), latitude: pick.latitude, longitude: pick.longitude })}>
                Guardar domicilio
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {saved ? <div role="status" style={{ fontSize: "13px", color: "var(--success, #1a7f37)" }}>Domicilio guardado.</div> : null}
      {error ? <div role="alert" style={{ fontSize: "13px", color: "var(--danger, #d1242f)" }}>{error}</div> : null}
    </div>
  );
}
